// serviceRows.jsx — field primitives + per-service row editors + the repeatable ServiceBlock.
import React, { useState, useRef } from "react";
import { Ico, docName, withDocName, DocPreviewModal } from "./primitives";
import { api } from "../api";
import { G, GL } from "./constants";

// ── Shared field primitives ──
export const TI = ({ val, onCh, ph }) => (
  <input
    value={val || ""}
    onChange={onCh}
    placeholder={ph}
    className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs outline-none focus:border-[rgb(var(--brand-rgb))] bg-white"
  />
);

// min: earliest selectable date (e.g. a return date can't be before departure).
export const DI = ({ val, onCh, min }) => (
  <input
    type="date"
    value={val || ""}
    onChange={onCh}
    min={min || undefined}
    className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs outline-none focus:border-[rgb(var(--brand-rgb))] bg-white"
  />
);

export const SI = ({ val, onCh, opts }) => (
  <select
    value={val || ""}
    onChange={onCh}
    className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs outline-none focus:border-[rgb(var(--brand-rgb))] bg-white"
  >
    <option value="">Status…</option>
    {opts.map((o) => (
      <option key={o} value={o}>{o}</option>
    ))}
  </select>
);

export const CF = ({ item, i, update }) => (
  <div className="grid grid-cols-3 gap-2 mt-2.5 pt-2.5 border-t border-slate-100">
    {[["costAmt", "Cost"], ["depositAmt", "Deposit"], ["paidAmt", "Paid"]].map(([k, l]) => (
      <div key={k}>
        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">{l}</p>
        <input
          type="number"
          placeholder="0"
          value={item[k] || ""}
          onChange={(e) => update(i, k, e.target.value)}
          className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-xs outline-none focus:border-[rgb(var(--brand-rgb))] bg-white"
        />
      </div>
    ))}
  </div>
);

export const FlightRow = ({ item, i, update }) => {
  const upd = (k, v) => update(i, k, v);
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <TI val={item.airline} onCh={(e) => upd("airline", e.target.value)} ph="Airline" />
        <TI val={item.pnr} onCh={(e) => upd("pnr", e.target.value)} ph="PNR" />
        <DI val={item.departureDate} onCh={(e) => upd("departureDate", e.target.value)} />
        <DI val={item.returnDate} onCh={(e) => upd("returnDate", e.target.value)} min={item.departureDate} />
        <SI val={item.status} onCh={(e) => upd("status", e.target.value)} opts={["pending", "confirmed", "cancelled", "completed"]} />
      </div>
      <CF item={item} i={i} update={update} />
    </>
  );
};

export const HotelRow = ({ item, i, update }) => {
  const upd = (k, v) => update(i, k, v);
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <TI val={item.name} onCh={(e) => upd("name", e.target.value)} ph="Hotel Name" />
        <TI val={item.confirmation} onCh={(e) => upd("confirmation", e.target.value)} ph="Confirmation #" />
        <DI val={item.checkIn} onCh={(e) => upd("checkIn", e.target.value)} />
        <DI val={item.checkOut} onCh={(e) => upd("checkOut", e.target.value)} min={item.checkIn} />
        <SI val={item.status} onCh={(e) => upd("status", e.target.value)} opts={["pending", "confirmed", "cancelled", "completed"]} />
      </div>
      <CF item={item} i={i} update={update} />
    </>
  );
};

export const VisaRow = ({ item, i, update }) => {
  const upd = (k, v) => update(i, k, v);
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <TI val={item.type} onCh={(e) => upd("type", e.target.value)} ph="Type (tourist…)" />
        <DI val={item.dateSubmitted} onCh={(e) => upd("dateSubmitted", e.target.value)} />
        <SI val={item.visaStatus} onCh={(e) => upd("visaStatus", e.target.value)} opts={["pending", "submitted", "approved", "rejected", "collected"]} />
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <TI val={item.documentsRequired} onCh={(e) => upd("documentsRequired", e.target.value)} ph="Docs required (comma sep.)" />
        <TI val={item.documentsSubmitted} onCh={(e) => upd("documentsSubmitted", e.target.value)} ph="Docs submitted" />
      </div>
      <CF item={item} i={i} update={update} />
    </>
  );
};

export const TourRow = ({ item, i, update }) => {
  const upd = (k, v) => update(i, k, v);
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <TI val={item.tourName} onCh={(e) => upd("tourName", e.target.value)} ph="Tour Name" />
        <DI val={item.tourDate} onCh={(e) => upd("tourDate", e.target.value)} />
        <SI val={item.status} onCh={(e) => upd("status", e.target.value)} opts={["pending", "confirmed", "cancelled", "completed"]} />
      </div>
      <CF item={item} i={i} update={update} />
    </>
  );
};

export const TransferRow = ({ item, i, update }) => {
  const upd = (k, v) => update(i, k, v);
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <DI val={item.bookingDate} onCh={(e) => upd("bookingDate", e.target.value)} />
        <SI val={item.status} onCh={(e) => upd("status", e.target.value)} opts={["pending", "confirmed", "cancelled", "completed"]} />
      </div>
      <CF item={item} i={i} update={update} />
    </>
  );
};

export const InsuranceRow = ({ item, i, update }) => {
  const upd = (k, v) => update(i, k, v);
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <TI val={item.provider} onCh={(e) => upd("provider", e.target.value)} ph="Provider" />
        <TI val={item.policyNumber} onCh={(e) => upd("policyNumber", e.target.value)} ph="Policy #" />
        <DI val={item.startDate} onCh={(e) => upd("startDate", e.target.value)} />
        <DI val={item.endDate} onCh={(e) => upd("endDate", e.target.value)} min={item.startDate} />
        <SI val={item.status} onCh={(e) => upd("status", e.target.value)} opts={["pending", "active", "expired", "cancelled"]} />
      </div>
      <CF item={item} i={i} update={update} />
    </>
  );
};

// ── Document uploader (manual entry stays; this is the "upload instead" option) ──
export const DocUploader = ({ docs = [], onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const list = (docs || []).filter(Boolean);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setErr("");
    setUploading(true);
    try {
      // One request per file so each URL is paired with ITS filename, which is kept
      // on the URL (after "#") so documents stay identifiable, e.g. "Passport - Ada.pdf".
      const urls = await Promise.all(
        files.map(async (f) => withDocName((await api.upload([f]))[0], f.name)),
      );
      onChange([...(docs || []), ...urls]);          // append (supports multiple)
    } catch (ex) {
      setErr(ex.message || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));

  return (
    <div className="mt-2.5 pt-2.5 border-t border-slate-100">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
          Documents <span className="text-slate-300 normal-case font-semibold">· optional, upload instead of typing</span>
        </p>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-lg transition-all disabled:opacity-50"
          style={{ color: G, background: GL }}
        >
          {uploading ? "Uploading…" : (
            <>
              <Ico d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M12 3v13m0-13l-4 4m4-4l4 4" sz={11} col={G} /> Upload
            </>
          )}
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,application/pdf"
        className="hidden"
        onChange={handleFiles}
      />

      {err && <p className="text-[10px] text-rose-500 mb-1.5">{err}</p>}

      {list.length ? (
        <div className="flex flex-wrap gap-1.5">
          {list.map((u, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-lg border border-slate-200 bg-white">
              <button type="button" onClick={() => setPreview(i)} className="max-w-[130px] truncate text-left" style={{ color: G }}>
                {docName(u)}
              </button>
              <button type="button" onClick={() => remove(i)} className="text-slate-300 hover:text-rose-500 transition-colors">✕</button>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-[10px] text-slate-400">No documents uploaded yet.</p>
      )}

      {preview !== null && (
        <DocPreviewModal docs={list} index={preview} onClose={() => setPreview(null)} />
      )}
    </div>
  );
};

export function ServiceBlock({ title, items, setItems, emptyRow, RowComponent }) {
  const add = () => setItems([...items, { ...emptyRow }]);
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const update = (i, k, v) =>
    setItems(items.map((item, idx) => (idx === i ? { ...item, [k]: v } : item)));

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
        <span className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-600">{title}</span>
        <button
          onClick={add}
          className="flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-lg transition-all"
          style={{ color: G, background: GL }}
          onMouseEnter={(e) => { e.currentTarget.style.background = G; e.currentTarget.style.color = "white"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = GL; e.currentTarget.style.color = G; }}
        >
          <Ico d="M12 4v16m8-8H4" sz={11} col="currentColor" /> Add
        </button>
      </div>
      {!items.length ? (
        <p className="text-xs text-slate-400 text-center py-3.5">No {title.toLowerCase()} added</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {items.map((item, i) => (
            <div key={i} className="p-3.5">
              <div className="flex justify-between items-center mb-2.5">
                <span className="text-xs font-bold text-slate-600">{title.replace(/s$/, "")} {i + 1}</span>
                <button onClick={() => remove(i)} className="text-xs text-slate-300 hover:text-rose-400 font-bold transition-colors">✕</button>
              </div>
              <RowComponent item={item} i={i} update={update} />
              <DocUploader docs={item.documents} onChange={(docs) => update(i, "documents", docs)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
